import { formatDistanceToNowStrict } from "date-fns";
import { fr } from "date-fns/locale";
import { Clock } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import type { RevenueReport } from "./types";

// Sprint 10.x Phase B — Historique des rapports stratégiques précédents.
// Affiche en colonne droite sur desktop, liste compacte avec
// "il y a Xj · période · secteurs". Click → sélectionne ce rapport pour
// affichage dans la carte principale (override du "dernier rapport").

interface StrategyHistoryProps {
  reports: RevenueReport[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}

function formatSectors(sectors: string[] | null | undefined): string {
  if (!sectors || sectors.length === 0) return "tous secteurs";
  return sectors.join("/");
}

export function StrategyHistory({
  reports,
  selectedId,
  onSelect,
}: StrategyHistoryProps) {
  if (reports.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-1.5 mb-3">
          <Clock className="size-3.5 text-muted-foreground" />
          <h2 className="text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
            Rapports précédents
          </h2>
        </div>
        <ul className="flex flex-col gap-1">
          {reports.map((report) => {
            const isSelected = report.id === selectedId;
            const rel = formatDistanceToNowStrict(
              new Date(report.generated_at),
              { addSuffix: true, locale: fr },
            );
            return (
              <li key={report.id}>
                <button
                  type="button"
                  onClick={() => onSelect(isSelected ? null : report.id)}
                  className={cn(
                    "w-full text-left rounded-md px-2.5 py-2 text-[12.5px] transition-colors",
                    isSelected
                      ? "bg-violet-100/60 dark:bg-violet-900/30 text-foreground"
                      : "hover:bg-muted/60 text-muted-foreground hover:text-foreground",
                  )}
                  aria-current={isSelected ? "true" : undefined}
                >
                  <div className="font-medium">{rel}</div>
                  <div className="text-[11.5px] text-muted-foreground mt-0.5">
                    {report.period_days}j · {formatSectors(report.sectors)}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
        {selectedId && (
          <div className="mt-3 pt-3 border-t">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="text-[11.5px] h-7 w-full"
              onClick={() => onSelect(null)}
            >
              Revenir au dernier rapport
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
